import { useMemo } from 'react'
import { gql } from 'generated/gql'
import { useQuery } from '@apollo/client'
import { GetClientMetadataQuery } from 'generated/graphql'
import { uniq } from 'lodash-es'
import { groupBy } from 'lib/utils'

export const GET_CLIENT_METADATA = gql(`
  query GetClientMetadata($ids: [ID!]!) {
    clients(ids: $ids) {
      metadata {
        statuses {
          key
          value
        }
        dispositions {
          key
          value
        }
      }
      customAttributeDefinitions {
        ...CustomAttributeDefinitionFields
      }
    }
  }
`)

export type MetadataValue = {
  key: string
  value: string
}

// FIXME: this is duplicated from Client.default_statuses
const DEFAULT_STATUSES = {
  pending:              'Pending',
  filtered:             'Filtered',
  rejected:             'Rejected',
  error:                'Error',
  returned:             'Returned',
  new_lead:             'New lead',
  contacted:            'Contacted',
  interviewed:          'Interviewed',
  qualified:            'Qualified',
  application_started:  'Application Started',
  applied:              'Applied',
  admitted:             'Admitted',
  confirmed_acceptance: 'Confirmed Acceptance',
  enrolled:             'Enrolled',
  started:              'Started',
  matriculated:         'Matriculated',
  class_passed:         'Class Passed',
  graduated:            'Graduated'
}

// FIXME: this is duplicated from Client.default_dispositions
const DEFAULT_DISPOSITIONS = {
  not_qualified:  'Not Qualified',
  not_interested: 'Not Interested',
  closed_lost:    'Closed Lost',
  bad_lead:       'Bad Lead',
}

function createLookup(loading: boolean, metadataValues: MetadataValue[], defaultValues: Record<string,string>) {
  if(loading) {
    return Object.fromEntries(Object.entries(defaultValues).map(([key, _value]) => [key, '...']))
  }
  else if(metadataValues.length > 0) {
    return Object.fromEntries(metadataValues.map(pair => [pair.key, pair.value]))
  }
  else {
    return defaultValues
  }
}

function merge(clients: GetClientMetadataQuery['clients'], metadataField: 'statuses' | 'dispositions', defaultValue: Record<string,string>) {
  const flattened: MetadataValue[] = clients.map(client => client.metadata[metadataField]).flat()
  return groupBy(flattened, 'key', { asEntries: true }).map(([key, metadataValues]: [string, MetadataValue[]]) => {
    const values = metadataValues.map(m => m.value)
    if(uniq(values).length === 1) {
      return { key: key, value: values[0] }
    }
    else {
      return { key: key, value: defaultValue[key] }
    }
  })
}

export default function useClientMetadata(clientIds: string[]) {
  const query = useQuery(GET_CLIENT_METADATA, {
    variables: {
      ids: clientIds,
    },
    skip: clientIds.length === 0,
  })

  return useMemo(() => {
    const clients = query?.data?.clients || []
    const customAttributeDefinitions = clients.map(client => client.customAttributeDefinitions).flat()
    const clientCampaignCustomAttributes = uniq(
      customAttributeDefinitions
        .filter(cad => cad.entityType === 'client_campaign')
        .map(attribute => attribute.key)
    )

    return {
      query,
      statuses: createLookup(query.loading, merge(clients, 'statuses', DEFAULT_STATUSES), DEFAULT_STATUSES),
      dispositions: createLookup(query.loading, merge(clients, 'dispositions', DEFAULT_DISPOSITIONS), DEFAULT_DISPOSITIONS),
      clientCampaignCustomAttributes: clientCampaignCustomAttributes,
    }
  }, [query])
}
